import objectPath from 'object-path'
import httpClient from 'utility/httpClient.util'
import { getDocumentsJson } from 'utility/documentsJsonCache.util'
import PNotify from 'pnotify/dist/es/PNotify'
import moment from 'moment'
import {
  compareTwoValue,
  generateString,
  getCheckValue,
  getCurrentUi,
} from './Data'
import { createOptionResponse } from './Design'
import cloneDeep from 'clone-deep'
import {
  createCustomRequest,
  createMultiSelectRequest,
  setRequestFormData,
} from '../element/button/Request'
import { isFilled } from './condition/State'
import { onCheckCondition } from './condition/Action'
import {
  applyRequestTimeout,
  getDesignerRequestVersion,
  isDesignerRequestVersionActive,
  withRequestTimeout,
} from './RequestTimeout'

export const serviceList = [
  { label: 'Accounting', name: 'accounting', path: '/accounting' },
  { label: 'Auto Sync', name: 'autosync', path: '/autosync' },
  { label: 'Adr', name: 'adr', path: '/adr' },
  { label: 'Calendar', name: 'calendar', path: '/calendar' },
  { label: 'Clinical', name: 'clinical', path: '/clinical' },
  { label: 'Core', name: 'core', path: '/core' },
  { label: 'Dental', name: 'dental', path: '/dental' },
  { label: 'Designer', name: 'designer', path: '/designer' },
  { label: 'Document', name: 'document', path: '/document' },
  { label: 'EHR', name: 'ehr', path: '/ehr' },
  { label: 'Finance', name: 'finance', path: '/finance' },
  { label: 'HR', name: 'hr', path: '/hr' },
  { label: 'IPD', name: 'ipd', path: '/ipd' },
  { label: 'Laboratory', name: 'laboratory', path: '/laboratory' },
  { label: 'Media', name: 'media', path: '/media' },
  { label: 'Nursing', name: 'nursing', path: '/nursing' },
  { label: 'Nutrition', name: 'nutrition', path: '/nutrition' },
  { label: 'Pas', name: 'pas', path: '/pas' },
  { label: 'Pharmacy', name: 'pharmacy', path: '/pharmacy' },
  { label: 'Radiology', name: 'radiology', path: '/radiology' },
  { label: 'Refer', name: 'refer', path: '/refer' },
  { label: 'Report', name: 'report', path: '/report' },
  { label: 'Stock', name: 'stock', path: '/stock' },
  { label: 'Surgery', name: 'surgery', path: '/surgery' },
]

const isNumber = (a) => Number(a) === +a

const isCheckedFormValue = (value) =>
  value === true || value === 1 || value === '1' || value === 'true'

const getFirstDefinedPathValue = (data = {}, paths = []) => {
  if (!data || typeof data !== 'object') return undefined

  let result

  paths.find((path) => {
    if (!path || !objectPath.has(data, path)) return false

    result = objectPath.get(data, path)
    return true
  })

  return result
}

const getFirstDefinedValueByKey = (data, targetKey, visited = []) => {
  if (!data || typeof data !== 'object' || !targetKey) return undefined
  if (visited.includes(data)) return undefined

  visited.push(data)

  if (Object.prototype.hasOwnProperty.call(data, targetKey)) {
    return data[targetKey]
  }

  let value

  Object.keys(data).find((key) => {
    value = getFirstDefinedValueByKey(data[key], targetKey, visited)

    return value !== undefined
  })

  return value
}

const getUiNameLastPart = (name = '') =>
  String(name || '')
    .split('.')
    .filter((item) => item)
    .pop() || ''

const getFormUiByName = (jsonForm = {}, name) => {
  let mapName = objectPath.get(jsonForm, 'schema.mapName') || {}
  let foundKey = Object.keys(mapName).find((key) => {
    let uiName = objectPath.get(mapName, key)

    return uiName === name || getUiNameLastPart(uiName) === name || key === name
  })

  let ui = foundKey
    ? getCurrentUi(foundKey, jsonForm.schema, jsonForm.uischema)
    : getCurrentUi(name, jsonForm.schema, jsonForm.uischema)

  if (!ui?.key && foundKey) {
    return {
      key: foundKey,
      name: objectPath.get(mapName, foundKey),
    }
  }

  return ui?.key ? ui : null
}

const getFormUiValue = (formData = {}, ui = {}) =>
  getFirstDefinedPathValue(formData, [ui.key, ui.name])

const getActionParamValueList = (params = []) => {
  if (!Array.isArray(params)) return []

  return params.reduce((list, param = {}) => {
    return [
      ...list,
      param.param,
      param.key,
      param.name,
      param.value,
      param.prefixValue,
      param.sourceKey,
      param.conditionValue,
    ]
  }, [])
}

const isHrStaffCodeSerialAction = (action = {}) => {
  let actionValues = [
    action.key,
    action.code,
    action.name,
    action.apiName,
    action.service,
    action.endpoint,
    action.api,
    action.path,
    action.url,
    `${action.service || ''}${action.endpoint || ''}`,
    ...getActionParamValueList(action.pathParams),
    ...getActionParamValueList(action.queryParams),
  ]

  return actionValues.some((value) =>
    String(value || '').includes('HR_STAFF_CODE'),
  )
}

const setEmployeeCodeSerialData = (action = {}, data) => {
  if (!isHrStaffCodeSerialAction(action)) return data
  if (!data || typeof data !== 'object') return data

  let employeeCode = getFirstDefinedPathValue(data, [
    'employeeCode',
    'data.employeeCode',
  ])
  if (
    employeeCode !== undefined &&
    employeeCode !== null &&
    employeeCode !== ''
  ) {
    return data
  }

  let serial = getFirstDefinedPathValue(data, ['serial', 'data.serial'])
  if (serial === undefined) return data

  objectPath.set(data, 'employeeCode', serial)
  let responseData = objectPath.get(data, 'data')
  if (responseData && typeof responseData === 'object') {
    objectPath.set(data, 'data.employeeCode', serial)
  }

  return data
}

const isEmployeeCodeField = (item = {}, ui = {}) => {
  let fieldList = [
    item.key,
    item.systemKey,
    item.response,
    item.value,
    item.element,
    item.fieldName,
    ui.name,
  ]
    .map((value) => getUiNameLastPart(value))
    .filter((value) => value)

  return fieldList.includes('employeeCode')
}

const getEmployeeCodeSerialValue = (
  action = {},
  item = {},
  data = {},
  ui = {},
) => {
  if (!isHrStaffCodeSerialAction(action)) return undefined
  if (!isEmployeeCodeField(item, ui)) return undefined

  return getFirstDefinedPathValue(data, [
    'employeeCode',
    'data.employeeCode',
    'serial',
    'data.serial',
  ])
}

const getCheckboxTargetName = (checkboxName = '') => {
  let name = getUiNameLastPart(checkboxName)
  if (!name.startsWith('is') || name.length <= 2) return ''

  let targetName = name.slice(2)

  return targetName.charAt(0).toLowerCase() + targetName.slice(1)
}

const getCheckedCheckboxList = (formData = {}, jsonForm = {}) => {
  let mapTag = objectPath.get(jsonForm, 'schema.mapTag') || {}

  return Object.keys(mapTag)
    .filter((key) => objectPath.get(mapTag, key) === 'checkbox')
    .map((key) => getCurrentUi(key, jsonForm.schema, jsonForm.uischema))
    .filter((ui) => ui?.key && isCheckedFormValue(getFormUiValue(formData, ui)))
}

const getMappedResponseSourceList = (data = {}, formData = {}) => [
  data,
  objectPath.get(data, 'data'),
  objectPath.get(data, 'value'),
  objectPath.get(formData, '@response'),
]

const getMappedResponseValueByName = (data = {}, formData = {}, name) => {
  let value

  getMappedResponseSourceList(data, formData).find((source) => {
    value = getFirstDefinedValueByKey(source, name)

    return value !== undefined
  })

  return value
}

const setMappedResponseFormValue = (formData = {}, ui = {}, value) => {
  if (ui.name) {
    objectPath.set(formData, ui.name, value)
  }

  if (ui.key) {
    objectPath.set(formData, ui.key, value)
    objectPath.set(formData, ['@mapResponse', ui.key], true)
  }

  return formData
}

const mapCheckedCheckboxResponseData = (
  formData = {},
  data = {},
  jsonForm = {},
) => {
  getCheckedCheckboxList(formData, jsonForm).forEach((checkboxUi) => {
    let targetName = getCheckboxTargetName(checkboxUi.name)
    if (!targetName) return

    let targetUi = getFormUiByName(jsonForm, targetName)
    if (!targetUi?.key) return

    let value = getMappedResponseValueByName(data, formData, targetName)
    if (value === undefined) return

    formData = setMappedResponseFormValue(formData, targetUi, value)
  })

  return formData
}

const createArrayTable = (action, formData, condition) => {
  let { key, subRequest, systemKey } = action
  if (!key && systemKey) {
    key = systemKey
  }

  let { deletedRow } = condition
  let tableData = objectPath.get(formData, key)
  let requestTable = []
  if (!Array.isArray(tableData)) return requestTable

  if (action.arrayTableType === 'detailPanel') {
    if (!action.detailPanelKey) return requestTable
    let detailPanelColumn = action?.detailPanelColumn || []
    tableData.forEach((rowData) => {
      if (deletedRow) {
        let isDeleted = objectPath.get(rowData, '@isDeleted')
        if (isDeleted) return
      }
      let detailRowValue = objectPath.get(rowData, action.detailPanelKey)
      if (Array.isArray(detailRowValue)) {
        detailRowValue.forEach((rowData) => {
          let itemRequest = {}
          subRequest.forEach((item) => {
            let rowValue = objectPath.get(rowData, item.value)
            if (item.valueType !== 'string') {
              rowValue = getCheckValue(rowValue)
            }
            if (rowValue === undefined) {
              let foundColumn = detailPanelColumn.find(
                ({ field }) => field === item.value,
              )
              let defaultValue = getCheckValue(foundColumn?.defaultValue)
              objectPath.set(itemRequest, item.request, defaultValue)
            } else {
              objectPath.set(itemRequest, item.request, rowValue)
            }
          })
          requestTable.push(itemRequest)
        })
      }
    })
  } else {
    tableData.forEach((rowData) => {
      if (deletedRow) {
        let isDeleted = objectPath.get(rowData, '@isDeleted')
        if (isDeleted) return
      }
      if (subRequest) {
        let itemRequest = {}
        subRequest.forEach((item) => {
          let rowValue = objectPath.get(rowData, item.value)
          if (item.valueType !== 'string') {
            rowValue = getCheckValue(rowValue)
          }
          if (rowValue !== undefined) {
            objectPath.set(itemRequest, item.request, rowValue)
          }
        })
        requestTable.push(itemRequest)
      }
    })
  }

  return requestTable
}

export const createInputValue = (action, inputValue) => {
  let { inputType } = action
  if (inputType === 'number') {
    inputValue = Number(inputValue)
  }
  return inputValue
}

export const createDatasource = (action, datasourceValue) => {
  let { subRequest, datasourceType } = action
  let dataValue = objectPath.get(datasourceValue, action.value)
  if (datasourceType === 'array') {
    if (!Array.isArray(dataValue)) {
      dataValue = [dataValue]
    }
    let request = []
    dataValue.forEach((rowValue) => {
      if (!rowValue) return
      if (subRequest) {
        let requestItem = {}
        subRequest.forEach(({ request, value, valueType }) => {
          let requestValue = objectPath.get(rowValue, value)
          if (valueType === 'number') {
            requestValue = getCheckValue(requestValue)
          }
          objectPath.set(requestItem, request, requestValue)
        })
        request.push(requestItem)
      } else {
        request.push(rowValue)
      }
    })
    return request
  }
  if (datasourceType === 'object') {
    let requestItem = {}
    subRequest.forEach(({ request, value, valueType }) => {
      let requestValue = objectPath.get(dataValue, value)
      if (valueType === 'number') {
        requestValue = getCheckValue(requestValue)
      }
      objectPath.set(requestItem, request, requestValue)
    })
    return requestItem
  }
  if (datasourceType === 'number' || datasourceType === 'boolean') {
    return getCheckValue(dataValue)
  }
  return dataValue
}

const setRequestData = (action, data, files, designContext) => {
  let { jsonForm, getCurrentUi, widgetDesign } = designContext
  let { formData, allParams, datasourceValue, dataset } = data
  let mapTag = objectPath.get(jsonForm, 'schema.mapTag')
  let allFormGroup = jsonForm.formGroup
  let requestData = {}
  let { requestDataType } = action
  let apiSchema = objectPath.get(action, 'internalApi.schema.properties')
  let requiredField = []
  if (requestDataType === 'customData') {
    let { customData = [], condition } = action
    customData.forEach((subAction) => {
      let { request, value, key = '', required, systemKey } = subAction
      value = createCustomRequest({
        mainAction: action,
        action: subAction,
        data,
        designContext,
        apiSchema,
      })
      if (required) {
        if (value === undefined || value === null || value === '') {
          requiredField.push(request)
        }
      }
      objectPath.set(requestData, request, value)
    })
  } else if (requestDataType === 'formData') {
    let formDataList = action?.formDataList
    if (formDataList) {
      formDataList.forEach(({ key }) => {
        let form = allFormGroup.find((form) => form.key === key)
        if (!form) return requestData
        if (form.isGroup) {
          let groupData = form.groupData || []
          groupData.forEach((subGroup) => {
            requestData = setRequestFormData({
              form: subGroup,
              data,
              requestBody: requestData,
              designContext,
              apiSchema,
            })
          })
        } else {
          requestData = setRequestFormData({
            form,
            data,
            requestBody: requestData,
            designContext,
            apiSchema,
          })
        }
      })
    }
  } else if (requestDataType === 'file') {
    requestData = files
  } else if (requestDataType === 'multiSelect') {
    requestData = createMultiSelectRequest({
      action,
      data,
      designContext,
      apiSchema,
    })
  } else if (requestDataType === 'bulkData') {
    let bulkKey = objectPath.get(action, 'bulkKey')
    let key = objectPath.get(action, 'bulkData.key')
    let apiKey = objectPath.get(action, 'bulkData.apiKey')

    let isResponse = objectPath.get(action, 'bulkData.isResponse')
    let bulkRequests = []
    let bulkItems = []
    if (isResponse) {
      bulkItems = objectPath.get(formData, `@response.${apiKey}.${key}`)
    } else {
      let ui = getCurrentUi(key)
      if (ui.key === key) {
        let isBrowseGallery = ui.galleryType === 'browse'
        if (isBrowseGallery) {
          bulkItems = objectPath.get(formData, `@browseGallery.${key}`)
        }
      } else {
        bulkItems = objectPath.get(formData, key)
      }
    }
    if (bulkItems) {
      bulkItems.forEach((item) => {
        let bulkRequest = {}
        bulkKey.forEach(
          ({ request, propType, value, key = '', isDynamic = false }) => {
            if (isDynamic) {
              value = objectPath.get(formData, request)
            } else if (propType === 'gallery') {
              value = objectPath.get(item, value)
            } else if (propType === 'input') {
              value = objectPath.has(formData, key)
                ? objectPath.get(formData, key)
                : objectPath.get(formData, request)
            } else if (propType === 'response') {
              value = objectPath.get(item, key)
            } else if (propType === 'system') {
              value = objectPath.get(formData, key)
            } else if (propType === 'urlParam') {
              value = objectPath.get(allParams, key)
            } else if (propType === 'datasource') {
              value = objectPath.get(datasourceValue, value)
            } else if (propType === 'dataset') {
              let foundDataset = dataset.find((data) => data.key === key)
              if (foundDataset) {
                let datasetValue = []
                let { elementKey, elementName, fields } = foundDataset
                let elementData = objectPath.has(formData, elementKey)
                  ? objectPath.get(formData, elementKey)
                  : objectPath.get(formData, elementName) || []
                elementData.forEach((rowData, index) => {
                  fields.forEach(({ field, valueType, value }) => {
                    if (valueType === 'system') {
                      let rowValue = objectPath.get(rowData, field)
                      objectPath.set(
                        datasetValue,
                        `${index}.${field}`,
                        rowValue,
                      )
                    } else if (valueType === 'custom') {
                      objectPath.set(datasetValue, `${index}.${field}`, value)
                    }
                  })
                })
                value = datasetValue
              } else {
                value = null
              }
            } else if (propType === 'dateTime') {
              let now = null
              if (value === 'currentDate') {
                now = moment().format('YYYY-MM-DD')
              } else if (value === 'currentTime') {
                now = moment().format('HH:mm:ss')
              } else if (value === 'currentDateTime') {
                now = moment().format('YYYY-MM-DD HH:mm:ss')
              }
              value = now
            }
            objectPath.set(bulkRequest, request, value)
          },
        )
        bulkRequests.push(bulkRequest)
      })
    }
    requestData = bulkRequests
  }
  return requestData
}

export const createApiSchema = async (action, designContext, resolver) => {
  let { documentJson, setDocumentJson } = designContext
  let { service, endpoint, method } = action
  method = method.toLowerCase()
  let apiSchema = {}
  let apiJsonSchema = objectPath.get(documentJson, service)
  if (!apiJsonSchema) {
    let schema = await getDocumentsJson(service).catch(() => null)
    if (schema?.paths) {
      const resolved = await resolver.resolve(schema)
      const response = resolved.result
      apiJsonSchema = response.paths
      objectPath.set(documentJson, service, apiJsonSchema)
      setDocumentJson(documentJson)
    }
  }
  let data = objectPath.get(apiJsonSchema, endpoint)
  if (data) {
    let json = objectPath.get(data, method)
    if (json) {
      if (method === 'get') {
        apiSchema = objectPath.get(
          json,
          'responses.default.content.application/json.schema',
        )
      } else if (
        method === 'post' ||
        method === 'put' ||
        method === 'patch' ||
        method === 'delete'
      ) {
        apiSchema = objectPath.get(
          json,
          'requestBody.content.application/json.schema',
        )
      }
    }
  }
  return apiSchema
}

export const getServiceRequest = async (
  url,
  optionData,
  selectUi,
  formData = {},
) => {
  let responseData = await httpClient
    .get(url)
    .then(({ data }) => data)
    .catch(() => null)

  if (responseData) {
    let { dataKey } = optionData
    dataKey = dataKey.replace('[today]', moment().format('YYYY-MM-DD'))
    let dataKeyList = dataKey.split('.')
    let newKey = []
    dataKeyList.forEach((key) => {
      if (key === '[today]') {
        key = moment().format('YYYY-MM-DD')
      } else if (key.startsWith('@')) {
        let replaceKey = key.replace('@', '')
        key = objectPath.has(formData, replaceKey)
          ? objectPath.get(formData, replaceKey)
          : key
      }
      newKey.push(key)
    })
    responseData = objectPath.get(responseData, newKey.join('.'))
    optionData.options = responseData
  } else {
    optionData.options = []
  }
  return optionData
}

export const getExternalRequest = async (url, optionData, selectUi) => {
  let responseData = await httpClient
    .get(url)
    .then(({ data }) => data)
    .catch(() => null)
  if (responseData) {
    let { dataKey } = optionData
    dataKey = dataKey.replace('[today]', moment().format('YYYY-MM-DD'))
    responseData = objectPath.get(responseData, dataKey)
    let optionKey = objectPath.get(responseData, [0]) || []
    optionData.options = responseData
    optionData.keyOptions = Object.keys(optionKey)
  } else {
    optionData.options = []
    optionData.keyOptions = []
  }
  return optionData
}

export const setHeadersConfig = (action) => {
  let { headers = [] } = action
  let config = {}

  if (headers.length > 0) {
    headers.forEach(({ key, value }) => {
      if (key) {
        objectPath.set(config, `headers.${key}`, value)
      }
    })
  }
  if (action.requestDataType === 'file') {
    objectPath.set(config, ['headers', 'content-type'], 'multipart/form-data')
  }
  return config
}

export const postData = async (action, data, designContext) => {
  let { jsonForm } = designContext
  let { formData, files } = data
  // set api
  let api = setApi({ ...action, jsonForm }, formData, data)
  if (!api) return {}
  // set request body
  let requestData = setRequestData(action, data, files, designContext)
  if (!requestData) return {}
  // set headers config
  let config = applyRequestTimeout(setHeadersConfig(action), action)
  // send request
  let response = await withRequestTimeout(
    httpClient.post(api, requestData, config),
    action,
  )
    .then(({ data }) => {
      return { status: 200, data: data, message: 'success' }
    })
    .catch(function (error) {
      if (error.response) {
        return {
          status: error.response.status,
          message: error.response.data,
          data: null,
        }
      }
    })
  return response
}

export const putData = async (action, data, designContext) => {
  let { jsonForm } = designContext
  let { formData, files } = data
  // set api
  let api = setApi({ ...action, jsonForm }, formData, data)
  if (!api) return {}
  // set request body
  let requestData = setRequestData(action, data, files, designContext)
  if (!requestData) return {}
  // set headers config
  let config = applyRequestTimeout(setHeadersConfig(action), action)
  // send request
  let response = await withRequestTimeout(
    httpClient.put(api, requestData, config),
    action,
  )
    .then(({ data }) => {
      return { status: 200, data: data, message: 'success' }
    })
    .catch(function (error) {
      if (error.response) {
        return {
          status: error.response.status,
          message: error.response.data,
          data: null,
        }
      }
    })
  return response
}

export const patchData = async (action, data, designContext) => {
  let { jsonForm } = designContext
  let { formData, files } = data
  // set api
  let api = setApi({ ...action, jsonForm }, formData, data)
  if (!api) return {}
  // set request body
  let requestData = setRequestData(action, data, files, designContext)
  if (!requestData) return {}
  // set headers config
  let config = applyRequestTimeout(setHeadersConfig(action), action)
  // send request
  let response = await withRequestTimeout(
    httpClient.patch(api, requestData, config),
    action,
  )
    .then(({ data }) => {
      return { status: 200, data: data, message: 'success' }
    })
    .catch(function (error) {
      if (error.response) {
        return {
          status: error.response.status,
          message: error.response.data,
          data: null,
        }
      }
    })
  return response
}

export const deleteData = async (action, data, designContext) => {
  let { jsonForm } = designContext
  let { formData } = data
  // set api
  let api = setApi({ ...action, jsonForm }, formData, data)
  if (!api) return {}
  // set headers config
  let config = applyRequestTimeout(setHeadersConfig(action), action)
  // send request
  let response = await withRequestTimeout(httpClient.put(api, config), action)
    .then(({ data }) => {
      return { status: 200, data: data, message: 'success' }
    })
    .catch(function (error) {
      if (error.response) {
        return {
          status: error.response.status,
          message: error.response.data,
          data: null,
        }
      }
    })
  return response
}

const mapResponseTableValue = (
  action,
  currentTable = [],
  comingTable = [],
  filterMapTable = [],
  jsonForm,
) => {
  let currentTableRefId = action?.currentTableRefId || action?.currentRefId
  let comingTableRefId = action?.comingTableRefId || action?.comingRefId
  if (!Array.isArray(comingTable)) return currentTable
  // Replace Row
  currentTable.map((currentData, index) => {
    let comingData
    if (!currentTableRefId && !comingTableRefId) {
      comingData = comingTable[index]
    } else {
      let currentRefId = objectPath.get(currentData, currentTableRefId)
      comingData =
        comingTable.find((comingData) => {
          let comingId = objectPath.get(comingData, comingTableRefId)
          if (currentRefId && comingId) {
            return getCheckValue(currentRefId) === getCheckValue(comingId)
          }
          return false
        }) || {}
    }
    if (comingData) {
      filterMapTable.forEach((mapTable) => {
        let responseName = mapTable.response
        let field = mapTable.value
        if (objectPath.has(comingData, responseName)) {
          let rowValue = objectPath.get(comingData, responseName)
          let ui = getCurrentUi(field, jsonForm.schema, jsonForm.uischema)
          objectPath.set(currentData, field, rowValue)
          objectPath.set(currentData, mapTable.response, rowValue)
          if (ui.key === field) {
            objectPath.set(currentData, ui.name, rowValue)
          }
        }
      })
    }
    return currentData
  })
  if (action?.isAddComingNewRow) {
    // Check if not exist => create new row
    let newTableComing = []
    comingTable.forEach((comingData, index) => {
      let comingRefId = objectPath.get(comingData, comingTableRefId)
      let currentData = currentTable.find((currentData) => {
        let currentId = objectPath.get(currentData, currentTableRefId)
        if (comingRefId && currentId) {
          return getCheckValue(comingRefId) === getCheckValue(currentId)
        }
        return false
      })
      if (!currentData) {
        let newData = {}
        filterMapTable.forEach((mapTable) => {
          let responseName = mapTable.response
          let field = mapTable.value
          if (objectPath.has(comingData, responseName)) {
            let rowValue = objectPath.get(comingData, responseName)
            let ui = getCurrentUi(field, jsonForm.schema, jsonForm.uischema)
            objectPath.set(newData, field, rowValue)
            objectPath.set(newData, mapTable.response, rowValue)
            if (ui.key === field) {
              objectPath.set(newData, ui.name, rowValue)
            }
          }
        })
        let genId = generateString(10)
        objectPath.set(newData, '@genId', genId)
        newTableComing.push(newData)
      }
    })
    return currentTable.concat(newTableComing)
  }
  return currentTable
}

const mapResponseBulkValue = (
  action,
  currentTable = [],
  comingTable = [],
  filterMapTable = [],
  jsonForm,
  bulkData,
) => {
  let { formData, bulkKey, bulkName } = bulkData || {}
  let currentTableRefId = action?.currentTableRefId || action?.currentRefId
  let comingTableRefId = action?.comingTableRefId || action?.comingRefId
  if (!Array.isArray(comingTable)) return formData
  currentTable.map((currentData, index) => {
    let currentRefId = objectPath.get(currentData, currentTableRefId)
    let comingData =
      comingTable.find((comingData) => {
        let comingId = objectPath.get(comingData, comingTableRefId)
        if (currentRefId && comingId) {
          return getCheckValue(currentRefId) === getCheckValue(comingId)
        }
        return false
      }) || {}
    if (comingData) {
      filterMapTable.forEach((mapTable) => {
        let responseName = mapTable.response
        let field = mapTable.value
        if (objectPath.has(comingData, responseName)) {
          let rowValue = objectPath.get(comingData, responseName)
          let ui = getCurrentUi(field, jsonForm.schema, jsonForm.uischema)
          objectPath.set(currentData, field, rowValue)
          objectPath.set(currentData, mapTable.response, rowValue)
          if (bulkData) {
            objectPath.set(formData, `${bulkKey}.${index}.${ui.name}`, rowValue)
            objectPath.set(
              formData,
              `${bulkName}.${index}.${ui.name}`,
              rowValue,
            )
          }
          if (ui.key === field) {
            objectPath.set(currentData, ui.name, rowValue)
          }
        }
      })
    }
    return formData
  })
  return formData
}

const setResponseData = (jsonForm, action, option, newResponseTable) => {
  let {
    jsonKey = '',
    mapParam = {},
    mapOther = {},
    mapData = {},
    mapArray = {},
    mapRadio = {},
    isDynamic,
    dynamicName,
    isSystem,
    formDataName,
    componentName,
    isWidget,
  } = option
  let paramList = Object.keys(mapParam)
  let { responseList = [] } = action

  if (isSystem) {
    paramList.forEach((key) => {
      let ui, mapName, mapWidget
      ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
      mapName = objectPath.get(jsonForm, 'schema.mapName') || {}
      mapWidget = objectPath.get(jsonForm, 'schema.mapWidget.name') || {}
      if (!ui?.key) return
      let isArray = mapArray[key]
      let isRadioField = mapRadio[key]
      let name = mapParam[key]
      let value = mapData[key] || mapName[key] || mapWidget[key] || name || ''
      let checkWidget = objectPath.get(option, `mapWidget.${key}`)
      let lastKey = value.split('.')
      let responseObj = {}
      if (lastKey) {
        lastKey = lastKey.pop()
      }
      let responseKey = jsonKey ? `${jsonKey}.${lastKey}` : lastKey
      let responseName = responseList.find((item) => item === responseKey)
      newResponseTable.push({
        ...responseObj,
        response: responseName || '',
        dataType: 'input',
        key: isDynamic ? dynamicName : key,
        fieldName: isWidget ? name : isRadioField ? key : ui.fieldName,
        element: value,
        value: value,
        isDynamic: isDynamic,
        isComponentData: !isSystem,
        formDataName: formDataName,
        componentName: componentName,
        isArray: isArray,
        widget: checkWidget,
      })
    })
    let otherList = Object.values(mapOther)
    otherList.forEach(({ name, dataType }) => {
      let responseKey = jsonKey ? `${jsonKey}.${name}` : name
      let value = name
      let responseObj = {}
      let element = ''
      if (dataType === 'prop') {
        element = action.componentProp
      } else if (dataType === 'urlParam') {
        element = dataType
      }
      newResponseTable.push({
        ...responseObj,
        response: name,
        dataType: dataType,
        element: element,
        value: value,
        isComponentData: !isSystem,
        formDataName: formDataName,
        componentName: componentName,
      })
    })
  } else {
    newResponseTable.push({
      response: option.key,
      dataType: 'input',
      key: option.key,
      element: option.key,
      value: option.key,
      isDynamic: isDynamic,
      isComponentData: !isSystem,
      formDataName: formDataName,
      componentName: componentName,
    })
  }
  return { responseTable: newResponseTable }
}

const getResponseTableItemKey = (item = {}) => JSON.stringify(item)

const createUniqueResponseTable = (responseTable = []) => {
  let responseKeyList = {}

  return responseTable.filter((item) => {
    let responseKey = getResponseTableItemKey(item)
    if (responseKeyList[responseKey]) return false

    responseKeyList[responseKey] = true
    return true
  })
}

const mapRadioResponseData = (formData, data, jsonForm) => {
  let formGroup = objectPath.get(jsonForm, 'formGroup') || []
  formGroup.forEach(({ mapRadio = {} }) => {
    Object.keys(mapRadio).forEach((key) => {
      if (!objectPath.has(data, key)) return
      objectPath.set(formData, key, objectPath.get(data, key))
    })
  })
  return formData
}

export const mapResponseData = (
  action,
  data,
  formData = {},
  isWaitUpsert,
  jsonForm,
  designContext,
) => {
  const mapFormData = objectPath.get(jsonForm, 'schema.mapFormData') || {}
  const {
    isUiChanged,
    setIsUiChanged,
    searchData,
    setSearchData,
    setFieldSearchData,
  } = designContext || {}
  let nextSearchData = searchData
  let isSearchDataUpdated = false
  const setMapSearchData = (ui, key, value, config = {}) => {
    if (config.isSearch && setSearchData) {
      if (!isSearchDataUpdated) {
        nextSearchData = cloneDeep(searchData || {})
      }
      objectPath.set(nextSearchData, key, value)
      isSearchDataUpdated = true
      return
    }

    if (setFieldSearchData) {
      setFieldSearchData(ui, key, value, config)
    }
  }

  if (action.dataKey) {
    data = objectPath.get(data, action.dataKey)
  }

  if (!data) return formData
  data = setEmployeeCodeSerialData(action, data)
  if (action.responseDataType === 'formData') {
    formData = mapRadioResponseData(formData, data, jsonForm)
  }
  let responseTable = cloneDeep(action?.dataTable || [])
  let optionTable = action?.optionTable || []
  if (action.responseDataType === 'table') {
    let { currentTableRefId, comingTableRefId, tableKey } = action
    if (!tableKey) return formData
    let tableUi = getCurrentUi(tableKey, jsonForm.schema, jsonForm.uischema)
    let tableName = tableUi.name || action.tableName
    let comingTable = data
    let filterMapTable = responseTable.filter(({ value }) => value)
    if (action.tableValueType === 'coming') {
      if (isWaitUpsert?.status) {
        if (!currentTableRefId || !comingTableRefId) return formData
        let currentTable = cloneDeep(isWaitUpsert.table) || []
        let upsertRow = cloneDeep(isWaitUpsert.rowData) || []
        currentTable = mapResponseTableValue(
          action,
          currentTable,
          comingTable,
          filterMapTable,
          jsonForm,
        )
        upsertRow = mapResponseTableValue(
          action,
          upsertRow,
          comingTable,
          filterMapTable,
          jsonForm,
        )
        objectPath.set(formData, `@isWaitUpsert.${isWaitUpsert.key}`, upsertRow)
        objectPath.set(
          formData,
          `@isConfirmUpsert.${isWaitUpsert.key}`,
          currentTable,
        )
        if (isWaitUpsert?.key !== tableKey) {
          objectPath.set(formData, tableKey, currentTable)
          if (tableName) {
            objectPath.set(formData, tableName, currentTable)
          }
        }
      } else {
        let tableData = tableKey ? objectPath.get(formData, tableKey) : []
        let currentTable = cloneDeep(tableData) || []
        currentTable = mapResponseTableValue(
          action,
          currentTable,
          comingTable,
          filterMapTable,
          jsonForm,
        )
        if (tableName) {
          objectPath.set(formData, tableName, currentTable)
        }
        objectPath.set(formData, tableKey, currentTable)
        if (setIsUiChanged && isUiChanged) {
          objectPath.set(isUiChanged, ['isRenderTable', tableKey], currentTable)
          setIsUiChanged(isUiChanged)
        }
      }
    } else {
      let { tableColumn = [] } = tableUi
      let filterTableColumn = tableColumn.filter(
        ({ defaultValue }) => defaultValue !== undefined || defaultValue !== '',
      )
      if (Array.isArray(comingTable)) {
        comingTable.map((comingData) => {
          if (!comingData) return
          filterMapTable.forEach((mapTable) => {
            let responseName = mapTable.response
            let field = mapTable.value
            if (objectPath.has(comingData, responseName)) {
              let rowValue = objectPath.get(comingData, responseName)
              objectPath.set(comingData, field, rowValue)
            }
          })
          filterTableColumn.forEach(({ field, defaultValue }) => {
            let rowValue = objectPath.get(comingData, field)
            if (rowValue === undefined) {
              defaultValue = getCheckValue(defaultValue)
              if (defaultValue !== undefined) {
                objectPath.set(comingData, field, defaultValue)
              }
            }
          })
          return comingData
        })
      }
      if (tableName) {
        objectPath.set(formData, tableName, comingTable)
      }
      objectPath.set(formData, tableKey, comingTable)
    }
    if (designContext) {
      onCheckCondition(designContext, formData, tableKey)
    }
  } else if (action.responseDataType === 'bulkData') {
    let { bulkData, bulkReplaceValueType } = action
    let bulkKey = bulkData?.key
    if (!bulkKey) return formData
    let ui = getCurrentUi(bulkKey, jsonForm.schema, jsonForm.uischema)
    let defaultBulk = data
    let filterMapTable = responseTable.filter(({ value }) => value)
    if (bulkReplaceValueType === 'coming') {
      let tableData = objectPath.get(formData, bulkKey) || []
      let currentBulk = cloneDeep(tableData) || []
      formData = mapResponseBulkValue(
        action,
        currentBulk,
        defaultBulk,
        filterMapTable,
        jsonForm,
        { formData, bulkKey, bulkName: ui.name },
      )
    } else {
      if (ui.key === bulkKey) {
        objectPath.set(formData, ui.name, defaultBulk)
      }
      objectPath.set(formData, bulkKey, defaultBulk)
    }
  } else if (action.responseDataType === 'formData') {
    let { formDataList = [] } = action
    let { formGroup } = jsonForm
    formDataList.forEach((form) => {
      if (!form) return
      let key = form.key
      let [widgetKey, formKey] = key.split('-')
      if (widgetKey && formKey) {
        let ui = getCurrentUi(widgetKey, jsonForm.schema, jsonForm.uischema)
        if (ui.key === widgetKey) {
          let uiName = ui.name
          let oldData = objectPath.get(formData, uiName)
          let newData = {
            ...oldData,
            ...data,
          }
          objectPath.set(formData, uiName, newData)
        }
      } else {
        let foundGroup = formGroup.find((group) => group.key === key)
        if (foundGroup) {
          let result = setResponseData(
            jsonForm,
            action,
            foundGroup,
            responseTable,
          )
          responseTable = result.responseTable
        }
      }
      objectPath.set(formData, form.key, data)
    })
  }
  responseTable = createUniqueResponseTable(responseTable)
  responseTable.forEach((item) => {
    let {
      response,
      dynamicName,
      isDynamic,
      isComponentData,
      componentName,
      isArray,
      dataType,
      widget,
    } = item
    let key = item.key || item.systemKey
    let value = response ? objectPath.get(data, response) : null
    if (isArray) {
      if (value && typeof value === 'object') {
        let arrayData = Array.isArray(value) ? value : Object.values(value)
        let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
        if (ui.key === key) {
          let { elements = [], param = [] } = ui
          if (item?.responseRefId && item?.subResponse) {
            let { responseRefId, subResponse = [] } = item
            subResponse.forEach(({ panelKey, conditionValue }) => {
              let foundValue = arrayData.find(
                (rowValue) =>
                  objectPath.get(rowValue, responseRefId) === conditionValue,
              )
              if (foundValue) {
                let filterKey = Object.keys(jsonForm.schema.mapKey).filter(
                  (key) => {
                    let fullKey = jsonForm.schema.mapKey[key]
                    let fullKeyList = fullKey.split('.')
                    return (
                      fullKeyList.includes(panelKey) &&
                      key !== panelKey &&
                      jsonForm.schema.mapFormData[key]
                    )
                  },
                )
                filterKey.forEach((key) => {
                  let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
                  if (ui.key === key) {
                    let value = objectPath.get(foundValue, ui.name)
                    objectPath.set(formData, key, value)
                  }
                })
              }
            })
          } else if (param.length > 0) {
            param.forEach(({ name, value, tabKey }) => {
              // set data to sub panel
              let foundValue = arrayData.find(
                (data) => objectPath.get(data, name) === value,
              )
              if (foundValue) {
                let foundElement = elements.find(({ key }) => tabKey === key)
                if (foundElement) {
                  let { elements = [] } = foundElement
                  elements.forEach(({ name, key }) => {
                    if (mapFormData[key]) {
                      let value = objectPath.get(foundValue, name)
                      objectPath.set(formData, key, value)
                    }
                  })
                }
              }
            })
          } else {
            elements.forEach(({ elements }, index) => {
              let foundValue = objectPath.get(arrayData, index)
              elements.forEach(({ name, key }) => {
                if (mapFormData[key]) {
                  let value = objectPath.get(foundValue, name)
                  objectPath.set(formData, key, value)
                }
              })
            })
          }
        }
      }
    } else {
      let ui = key
        ? getCurrentUi(key, jsonForm.schema, jsonForm.uischema) || {}
        : {}
      if (key && !ui.key) {
        ui = getFormUiByName(jsonForm, key) || {}
      }
      if (response && !ui.key) {
        ui = getFormUiByName(jsonForm, response) || {}
      }
      if (key && !ui.name) {
        let uiName = objectPath.get(jsonForm, ['schema', 'mapName', key])
        if (uiName) {
          ui = { ...ui, key, name: uiName }
        }
      }
      let employeeCodeSerialValue = getEmployeeCodeSerialValue(
        action,
        item,
        data,
        ui,
      )
      let hasEmployeeCodeSerialValue = employeeCodeSerialValue !== undefined
      if (
        hasEmployeeCodeSerialValue &&
        (value === undefined || value === null || value === '')
      ) {
        value = employeeCodeSerialValue
      }
      if (value === undefined && !hasEmployeeCodeSerialValue) return

      let isTable = ui.tagname === 'table'
      let isWidget = dataType === 'widget'
      if (widget) {
        let { itemName, name, isDynamic, dynamicName } = widget
        if (isDynamic) {
          let value = objectPath.get(data, dynamicName)
          objectPath.set(formData, `${name}.${dynamicName}`, value)
        } else {
          let value = objectPath.get(data, itemName)
          objectPath.set(formData, `${name}.${itemName}`, value)
        }
      } else if (isWidget) {
        objectPath.set(formData, key, value)
      } else if (isTable) {
        let { tableValueType, subResponse = [] } = item
        let filterMapTable = subResponse.filter(({ value }) => value)
        let comingTable = value
        if (tableValueType === 'coming') {
          let currentTable = objectPath.get(formData, key) || []
          currentTable = mapResponseTableValue(
            item,
            currentTable,
            comingTable,
            filterMapTable,
            jsonForm,
          )
          if (ui.key === key) {
            objectPath.set(formData, ui.name, currentTable)
          }
          objectPath.set(formData, key, currentTable)
        } else {
          if (comingTable) {
            comingTable.map((comingData) => {
              filterMapTable.forEach((mapTable) => {
                let responseName = mapTable.response
                let field = mapTable.value
                if (objectPath.has(comingData, responseName)) {
                  let rowValue = objectPath.get(comingData, responseName)
                  objectPath.set(comingData, field, rowValue)
                }
              })
              return comingData
            })
          }
          if (ui.key === key) {
            objectPath.set(formData, ui.name, comingTable)
          }
          objectPath.set(formData, key, comingTable)
        }
      } else if (isDynamic) {
        objectPath.set(formData, dynamicName, value)
        objectPath.set(formData, item.key, value)
      } else if (isComponentData && componentName) {
        objectPath.set(formData, `${componentName}.${response}`, value)
      } else if (key && (response || hasEmployeeCodeSerialValue)) {
        objectPath.set(formData, key, value)
        if (ui.key) {
          objectPath.set(formData, ui.key, value)
          objectPath.set(formData, ['@mapResponse', ui.key], true)
          setMapSearchData(ui, ui.key, value, { isSearch: true })
        }
        if (ui.name) {
          objectPath.set(formData, ui.name, value)
          objectPath.set(formData, ['@mapResponse', key], true)
          setMapSearchData(ui, ui.name, value, { isSearch: true })
        }
        // objectPath.set(formData, response, value)
      } else if (response) {
        objectPath.set(formData, response, value)
        objectPath.set(formData, dynamicName, value)
      } else if (key && data) {
        if (key === ui.key && ui.tagname === 'dynamic') {
          objectPath.set(formData, ui.name, data)
          objectPath.set(formData, key, data)
        }
      }
    }
  })
  formData = mapCheckedCheckboxResponseData(formData, data, jsonForm)
  if (optionTable.length > 0) {
    createOptionResponse(optionTable, data, jsonForm)
  }
  if (isHrStaffCodeSerialAction(action) && setIsUiChanged) {
    setIsUiChanged({
      ...isUiChanged,
      mapResponse: new Date().getTime(),
    })
  }
  if (isSearchDataUpdated) {
    setSearchData(nextSearchData)
  }
  return formData
}

export const getData = async (
  action,
  data,
  designContext,
  isReturnResponse,
) => {
  const requestVersion = getDesignerRequestVersion()
  let { jsonForm, setDatasourceValue, setFieldSearchData } = designContext
  let designerPropsData = objectPath.get(jsonForm, 'designerPropsData') || {}
  let { responseDataType, customName, dataKey = '', dataTable } = action
  let { formData, datasourceValue } = data

  let api = setApi(action, formData, data)
  if (!api) return

  // set headers config
  let config = applyRequestTimeout(setHeadersConfig(action), action)
  // send request
  let response = await withRequestTimeout(httpClient.get(api, config), action)
    .then(({ data }) => {
      return { status: 200, data: data, message: 'success' }
    })
    .catch(function (error) {
      if (error.response) {
        return {
          status: error.response.status,
          message: error.response.data,
          data: null,
        }
      }
    })
  if (!response || !isDesignerRequestVersionActive(requestVersion)) return

  if (isReturnResponse) {
    return response
  }

  if (response?.status !== 200) {
    let propType = objectPath.get(designerPropsData, `propType`) || {}
    let filterProp = Object.keys(propType)
    filterProp.forEach((key) => {
      let [componentKey, dataKey] = key.split('/')
      if (propType[key] === 'formData') {
        objectPath.set(designerPropsData, `data.${componentKey}.${dataKey}`, {})
      } else {
        objectPath.set(designerPropsData, `data.${componentKey}.${dataKey}`, '')
      }
    })
    return response
  }
  let responseData = objectPath.get(response.data, dataKey)
  responseData = setEmployeeCodeSerialData({ ...action, api }, responseData)
  objectPath.set(datasourceValue, `${action.key}`, responseData)
  setDatasourceValue(datasourceValue)

  let internalSchema =
    objectPath.get(action, 'internalApi.schema.properties') || {}

  // set form data
  if (responseDataType === 'customData') {
    dataTable.forEach((data) => {
      let value = objectPath.get(responseData, data.response)
      let valueType = internalSchema[data.response]?.type
      if (value === undefined) {
        if (valueType === 'string') {
          value = ''
        } else if (valueType === 'array') {
          value = []
        } else if (valueType === 'object') {
          value = {}
        } else {
          value = null
        }
      }
      if (data.key && data.response) {
        objectPath.set(formData, data.key, value)
      }
      if (data.response) {
        objectPath.set(formData, data.response, value)
      }
    })
    if (customName) {
      objectPath.set(formData, `props.${formData}`, responseData || {})
    }
  } else if (responseDataType === 'formData') {
    dataTable.forEach(
      ({
        response,
        key,
        dynamicName,
        isDynamic,
        isComponentData,
        componentName,
      }) => {
        let value = objectPath.get(responseData, response)
        let valueType = internalSchema[response]?.type
        if (value === undefined) {
          if (valueType === 'string') {
            value = ''
          } else if (valueType === 'array') {
            value = []
          } else if (valueType === 'object') {
            value = {}
          } else {
            value = null
          }
        }
        if (isDynamic) {
          objectPath.set(formData, dynamicName, value)
        } else if (isComponentData) {
          objectPath.set(formData, `${componentName}.${response}`, value)
        } else if (key && response) {
          objectPath.set(formData, key, value)
          let ui =
            getFormUiByName(jsonForm, key) ||
            getFormUiByName(jsonForm, response)
          if (ui?.key) {
            objectPath.set(formData, ui.key, value)
            objectPath.set(formData, ['@mapResponse', ui.key], true)
            if (setFieldSearchData) {
              setFieldSearchData(ui, ui.key, value, { isSearch: true })
            }
          }
          if (ui?.name) {
            objectPath.set(formData, ui.name, value)
            if (setFieldSearchData) {
              setFieldSearchData(ui, ui.name, value, { isSearch: true })
            }
          }
        } else if (response) {
          objectPath.set(formData, response, value)
        }
      },
    )
  }
  response.data = formData
  objectPath.set(response, 'data', formData)
  return response
}

const isFile = (file) => 'File' in window && file instanceof File

const checkFileType = (value) => {
  if (Array.isArray(value)) {
    let foundFile = value.filter((file) => isFile(file)) || []
    if (foundFile.length > 0) {
      return { isFile: true, hasFile: foundFile.length > 0 }
    }
    return { isFile: false }
  }
  return { isFile: false }
}

export const checkConditionApi = (
  action,
  urlParams,
  formData,
  datasourceValue = {},
  tabParamActive = {},
  isWarning = false,
) => {
  let isPassed = true

  let hasFree = objectPath.get(action, 'condition.free')
  let hasRouter = objectPath.get(action, 'condition.router')
  let hasProp = objectPath.get(action, 'condition.props')
  let hasDatasource = objectPath.get(action, 'condition.datasource')
  let hasTab = objectPath.get(action, 'condition.tab')
  let hasFormData = objectPath.get(action, 'condition.formData')
  let hasResponse = objectPath.get(action, 'condition.response')
  let hasDeletedRow = objectPath.get(action, 'condition.deletedRow')

  if (
    !hasFree &&
    !hasRouter &&
    !hasProp &&
    !hasDatasource &&
    !hasTab &&
    !hasFormData &&
    !hasResponse &&
    !hasDeletedRow
  )
    return false

  if (hasFree) return true

  let isPassListed = []
  let conditionFailed = []

  if (hasRouter) {
    let routerParam = objectPath.get(action, 'routerParam') || []
    if (routerParam.length === 0) {
      isPassed = false
    } else {
      routerParam.forEach(({ param, hasValue, check }) => {
        let foundValue
        if (objectPath.has(urlParams, param)) {
          foundValue = objectPath.get(urlParams, param)
        }
        if (hasValue) {
          if (foundValue === check) {
            isPassListed.push(true)
          } else {
            isPassListed.push(false)
          }
        } else {
          if (foundValue === check) {
            isPassListed.push(true)
          } else {
            isPassListed.push(false)
          }
        }
      })
    }
  }

  if (hasProp) {
    let propParam = objectPath.get(action, 'propParam') || []
    let condition = objectPath.get(formData, `@condition`) || {}
    if (propParam.length === 0) {
      isPassed = false
    } else {
      propParam.forEach(({ prop, hasValue, conditionValue }) => {
        let dataValue =
          objectPath.get(condition, prop) || objectPath.get(formData, prop)
        let { isFile, hasFile } = checkFileType(dataValue)
        if (isFile) {
          if (hasFile) {
            isPassListed.push(true)
          } else {
            isPassListed.push(false)
          }
        } else {
          let foundValue = isFilled(dataValue)
          if (hasValue && foundValue) {
            if (conditionValue === '' || conditionValue === undefined) {
              isPassListed.push(true)
            } else {
              let pass = compareTwoValue(
                'equalValue',
                conditionValue,
                dataValue,
              )
              isPassListed.push(pass)
              if (!pass) {
                conditionFailed.push(`<li class="mb-0">${prop}</li>`)
              }
            }
          } else if (!hasValue && !foundValue) {
            isPassListed.push(true)
          } else {
            isPassListed.push(false)
            conditionFailed.push(`<li class="mb-0">${prop}</li>`)
          }
        }
      })
    }
  }
  if (hasDatasource) {
    let datasourceParam = objectPath.get(action, 'datasourceParam') || []
    if (datasourceParam.length === 0) {
      isPassed = false
    } else {
      datasourceParam.forEach(
        ({ datasource, hasValue, value, conditionValue }) => {
          conditionValue = conditionValue || value
          let dataValue = objectPath.get(datasourceValue, datasource)
          let { isFile, hasFile } = checkFileType(dataValue)
          if (isFile) {
            if (hasFile) {
              isPassListed.push(true)
            } else {
              isPassListed.push(false)
            }
          } else {
            let foundValue = isFilled(dataValue)
            if (hasValue && foundValue) {
              if (conditionValue === '' || conditionValue === undefined) {
                isPassListed.push(true)
              } else {
                let pass = compareTwoValue(
                  'equalValue',
                  conditionValue,
                  dataValue,
                )
                isPassListed.push(pass)
                if (!pass) {
                  conditionFailed.push(`<li class="mb-0">${datasource}</li>`)
                }
              }
            } else if (!hasValue && !foundValue) {
              isPassListed.push(true)
            } else {
              isPassListed.push(false)
              conditionFailed.push(`<li class="mb-0">${datasource}</li>`)
            }
          }
        },
      )
    }
  }

  if (hasTab) {
    let tabParam = objectPath.get(action, 'tabParam') || []
    if (tabParam.length === 0) {
      isPassed = false
    } else {
      tabParam.forEach(({ mainTab, tabKey, param }) => {
        let paramList = tabParamActive[mainTab] || []
        let found = paramList.find(
          (active) => active.tabKey === tabKey && active.name === param,
        )
        if (!found) {
          isPassListed.push(false)
          conditionFailed.push(`<li class="mb-0">${tabKey} > ${param}</li>`)
        }
      })
    }
  }

  if (hasFormData) {
    let dataParam = objectPath.get(action, 'dataParam') || []
    if (dataParam.length === 0) {
      isPassed = false
    } else {
      dataParam.forEach(
        ({ key, value, hasValue, formComponentName, conditionValue }) => {
          let dataKey = formComponentName ? `${formComponentName}.${key}` : key
          let dataName = formComponentName
            ? `${formComponentName}.${value}`
            : value
          let dataValue =
            objectPath.get(formData, dataKey) ||
            objectPath.get(formData, dataName) ||
            objectPath.get(formData, key)
          let { isFile, hasFile } = checkFileType(dataValue)
          if (isFile) {
            if (hasFile) {
              isPassListed.push(true)
            } else {
              isPassListed.push(false)
              conditionFailed.push(`<li class="mb-0">${value}</li>`)
            }
          } else {
            let foundValue = isFilled(dataValue)
            if (hasValue && foundValue) {
              if (conditionValue === '' || conditionValue === undefined) {
                isPassListed.push(true)
              } else {
                let pass = compareTwoValue(
                  'equalValue',
                  conditionValue,
                  dataValue,
                )

                isPassListed.push(pass)
                if (!pass) {
                  conditionFailed.push(`<li class="mb-0">${dataName}</li>`)
                }
              }
            } else if (!hasValue && !foundValue) {
              isPassListed.push(true)
            } else {
              isPassListed.push(false)
              conditionFailed.push(`<li class="mb-0">${dataName}</li>`)
            }
          }
        },
      )
    }
  }

  if (hasResponse) {
    let responseParam = objectPath.get(action, 'responseParam') || []
    let responseData = objectPath.get(formData, '@response') || {}
    if (responseParam.length === 0) {
      isPassed = false
    } else {
      responseParam.forEach(
        ({ response, responseName, hasValue, conditionValue }) => {
          let dataValue = objectPath.get(responseData, response)
          let foundValue = isFilled(dataValue)
          if (hasValue && foundValue) {
            if (conditionValue === '' || conditionValue === undefined) {
              isPassListed.push(true)
            } else {
              let pass = compareTwoValue(
                'equalValue',
                conditionValue,
                dataValue,
              )
              isPassListed.push(pass)
              if (!pass) {
                conditionFailed.push(`<li class="mb-0">${responseName}</li>`)
              }
            }
          } else if (!hasValue && !foundValue) {
            isPassListed.push(true)
          } else {
            isPassListed.push(false)
            conditionFailed.push(`<li class="mb-0">${responseName}</li>`)
          }
        },
      )
    }
  }

  if (hasDeletedRow) {
    isPassListed.push(true)
  }
  isPassed =
    isPassListed.length > 0 && isPassListed.includes(false) ? false : true

  if (isWarning && !isPassed) {
    let { apiType, service, endpoint, url } = action
    let api = apiType === 'internal' ? service + endpoint : url
    let notice = PNotify.notice({
      title: 'Skip API Call',
      text:
        `<p class="wrap mb-1" contenteditable="true"><strong>API:</strong> ${api}</p>` +
        `<p class="mb-0"><strong>เงื่อนไขที่ยังไม่ครบ</strong></p>` +
        `<ol style="padding-left:1.5rem;font-size:14px">` +
        conditionFailed.join('') +
        `</ol>`,
      textTrusted: true,
      maxTextHeight: null,
      icon: 'fas fa-exclamation-circle fa-xl',
    })
    notice.on('click', function () {
      notice.close()
    })
  }
  return isPassed
}

export const checkConditionNextApi = (action, formData, isWarning = false) => {
  let isPassed = true
  let hasResponse = objectPath.get(action, 'condition.response')
  let conditionFailed = []
  let isPassListed = []

  if (hasResponse) {
    let responseParam = objectPath.get(action, 'responseParam')
    let responseData = objectPath.get(formData, '@response')
    responseParam.forEach(
      ({ response, responseName, hasValue, conditionValue }) => {
        let dataValue = objectPath.get(responseData, response)
        let foundValue = isFilled(dataValue)
        if (hasValue && foundValue) {
          if (conditionValue === '' || conditionValue === undefined) {
            isPassListed.push(true)
          } else {
            let pass = compareTwoValue('equalValue', conditionValue, dataValue)
            isPassListed.push(pass)
            if (!pass) {
              conditionFailed.push(`<li class="mb-0">${responseName}</li>`)
            }
          }
        } else if (!hasValue && !foundValue) {
          isPassListed.push(true)
        } else {
          isPassListed.push(false)
          conditionFailed.push(`<li class="mb-0">${responseName}</li>`)
        }
      },
    )
    isPassed = isPassListed.includes(false) ? false : true
  }

  if (isWarning && !isPassed) {
    let { apiType, service, endpoint, url } = action
    let api = apiType === 'internal' ? service + endpoint : url
    let notice = PNotify.notice({
      title: 'Skip API Call',
      text:
        `<p class="wrap mb-1" contenteditable="true"><strong>API:</strong> ${api}</p>` +
        `<p class="mb-0"><strong>เงื่อนไขที่ยังไม่ครบ</strong></p>` +
        `<ol style="padding-left:1.5rem;font-size:14px">` +
        conditionFailed.join('') +
        `</ol>`,
      textTrusted: true,
      maxTextHeight: null,
      icon: 'fas fa-exclamation-circle fa-xl',
    })
    notice.on('click', function () {
      notice.close()
    })
  }
  return isPassed
}

const checkAPI = (apiData) => {
  let { apiType, api = '', service = '', endpoint = '' } = apiData
  if (!apiType) return null
  if (apiType === 'core' || apiType === 'internal') {
    api = service + endpoint
  }
  return api
}

export const checkPropTab = (data, { containerKey, paramName }) => {
  let { designContext = {} } = data
  let { tabParamActive } = designContext
  let tabData = objectPath.get(tabParamActive, containerKey)
  if (tabData) {
    let found = tabData.find((tab) => tab.name === paramName)
    let value = found?.value
    return isNumber(value) ? Number(value) : value
  }
  return
}

const checkPropTable = (data, { key, param }) => {
  let { rowData } = data
  return objectPath.get(rowData, key) || objectPath.get(rowData, param)
}

const checkPropUrlParam = (data, { key, param }) => {
  let { urlParams = {}, dataGET = {}, designContext = {} } = data
  let allParams = objectPath.get(designContext, 'urlParams') || {}
  return (
    objectPath.get(urlParams, key) ||
    objectPath.get(allParams, key) ||
    objectPath.get(dataGET, param)
  )
}

const checkPropSearch = (data, { customName, key, value }) => {
  let { formData = {}, designContext = {}, fetchSearch = {} } = data
  let { searchData } = designContext
  if (customName) {
    return (
      objectPath.get(fetchSearch, customName) ||
      objectPath.get(searchData, customName) ||
      objectPath.get(formData, customName)
    )
  }
  return (
    objectPath.get(fetchSearch, key) ||
    objectPath.get(fetchSearch, value) ||
    objectPath.get(searchData, key) ||
    objectPath.get(searchData, value)
  )
}

const checkPropSearchField = (apiData, data, { param }) => {
  let searchField = (apiData?.field || []).find(
    ({ name, field }) => name === param || field === param,
  )
  if (!searchField) return

  return checkPropSearch(data, {
    key: searchField.key,
    value: searchField.name,
  })
}

const checkPropInput = (data, { key, value, param, customName }) => {
  let { formData = {} } = data
  if (customName) {
    return objectPath.get(formData, customName)
  }
  return (
    objectPath.get(formData, key) ||
    objectPath.get(formData, value) ||
    objectPath.get(formData, param)
  )
}

const checkPropFormData = (data, { key, value, param }) => {
  let { formData = {}, dataGET = {} } = data
  return (
    objectPath.get(formData, key) ||
    objectPath.get(formData, value) ||
    objectPath.get(formData, param) ||
    objectPath.get(dataGET, param)
  )
}

const checkPropSystem = (data, { key }) => {
  let { designContext = {} } = data
  let { jsonForm } = designContext
  let systemData = jsonForm.systemData
  return objectPath.get(systemData, key) || null
}

const checkPropDatasource = (data, { sourceKey, key }) => {
  let datasourceKey = `${sourceKey}.${key}`
  let { designContext = {} } = data
  let datasourceValue = objectPath.get(designContext, `@datasource`) || {}
  return objectPath.get(datasourceValue, datasourceKey)
}

const checkPropPostgrestql = (data, { paramList, value }) => {
  let paramValue = value
  let { formData = {}, dataGET = {}, allParams = {}, urlParams = {} } = data
  let stringValue = ''
  let result = ''
  paramList.forEach(({ param, key, propType = '', value }) => {
    if (propType === 'urlParam') {
      if (objectPath.has(urlParams, key)) {
        stringValue = objectPath.get(urlParams, param)
      } else if (objectPath.has(allParams, key)) {
        stringValue = objectPath.get(allParams, param)
      } else if (objectPath.has(dataGET, param)) {
        stringValue = objectPath.get(dataGET, param)
      } else {
        stringValue = null
      }
    } else if (propType === 'input') {
      stringValue =
        objectPath.get(formData, key) ||
        objectPath.get(formData, param) ||
        objectPath.get(dataGET, param) ||
        value
    } else if (propType === 'custom') {
      stringValue = value
    }
    if (stringValue === undefined) {
      stringValue = ''
    }
    result = paramValue.replace(`$${param}`, stringValue)
    paramValue = result
  })
  return result
}

const checkPropScoreChart = (data, { key, param }) => {
  let { allParams = {} } = data
  return objectPath.get(allParams, key ?? param)
}

const checkPropListID = (data, idRef) => {
  let { listValue = {} } = data
  return idRef ? objectPath.get(listValue, idRef) : null
}

const checkPropVariables = (data, { key }) => {
  let { formData = {}, filterValue = {} } = data
  return objectPath.get(formData, key) || objectPath.get(filterValue, key)
}

const checkPropAutofill = (data, { key }) => {
  let { formData = {} } = data
  return objectPath.get(formData, key)
}

const checkPropDashboard = (filterValue, { variable }) => {
  return objectPath.get(filterValue, variable) || null
}

const checkPropFileId = (data) => {
  let { fileId } = data
  return fileId
}

const checkPropPage = (data) => {
  let { page } = data
  return page
}

const checkPropPerPage = (data) => {
  let { perPage } = data
  return perPage
}

const replacePathParamValue = (api = '', pathParam, value) => {
  value = getQueryPrimitiveValue(value, pathParam)
  if (value === null || value === '' || value === undefined) {
    return
  }
  let { param, prefixValue } = pathParam
  if (value && prefixValue && !String(value).startsWith(prefixValue)) {
    value = prefixValue + value
  }
  if (api.search(`:${param}`) > -1) {
    return api.replace(`:${param}`, value)
  }
  if (api.search(`{${param}}`) > -1) {
    return api.replace(`{${param}}`, value)
  }
  return api
}

const getQueryPrimitiveValue = (value, paramData = {}) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => getQueryPrimitiveValue(item, paramData))
      .filter((item) => item !== null && item !== undefined && item !== '')
      .join(',')
  }

  if (value && typeof value === 'object') {
    if (value.isAll) return ''
    let { param, key, name } = paramData
    let valueKey = [param, key, name]
      .filter((key) => key !== undefined && key !== null && key !== '')
      .find((key) => objectPath.has(value, key))
    if (valueKey) {
      let fieldValue = objectPath.get(value, valueKey)
      if (fieldValue !== value) {
        return getQueryPrimitiveValue(fieldValue, paramData)
      }
      return ''
    }
    if (value.format !== undefined) return value.format
    if (value.value !== undefined) return value.value
    if (value.label !== undefined) return value.label
    return ''
  }

  return value
}

const replaceQueryParamValue = (query, queryParam, value) => {
  value = getQueryPrimitiveValue(value, queryParam)
  if (value === null || value === '' || value === undefined) {
    return query
  }

  let { param, prefixValue } = queryParam
  if (value && prefixValue && !String(value).startsWith(prefixValue)) {
    value = prefixValue + value
  }
  if (value !== null && value !== undefined) {
    query.push(`${param}=${value}`)
  }
  return query
}

const replaceQueryApi = (api, apiData, queryString, data) => {
  let { showAllResult, pagination, isTable } = data
  let { enablePaginationPath, enablePaginationQuery, query, path } = apiData
  let connect = api.indexOf('?') > -1 ? '&' : '?'
  if (!isTable) {
    api = queryString.length > 0 ? api + connect + queryString.join('&') : api
    return api
  }
  if (enablePaginationPath) {
    if (path.page) {
      api = api.replace(`:${path.page}`, pagination.page)
    }
    if (path.rowsPerPage) {
      api = api.replace(`:${path.rowsPerPage}`, pagination.rowsPerPage)
    }
  }
  let queryPagination = []
  if (enablePaginationQuery) {
    if (pagination.page) {
      queryPagination.push(`${query.page}=${pagination.page}`)
    }
    if (pagination.rowsPerPage) {
      queryPagination.push(`${query.rowsPerPage}=${pagination.rowsPerPage}`)
    }
  }
  if (showAllResult) {
    api =
      [...queryString, ...queryPagination].length > 0
        ? api + connect + [...queryString, ...queryPagination].join('&')
        : api
  } else {
    api = api + connect + [...queryString, ...queryPagination].join('&')
  }
  return api
}

const checkPropDateTime = (data, { param, key }) => {
  let { formData = {}, filterValue = {} } = data
  return objectPath.get(formData, param) || objectPath.get(filterValue, param)
}

const checkPropValue = (apiData, data, paramData) => {
  let { idRef } = apiData
  let { param, propType, isAutoFill = false, value } = paramData
  if (isAutoFill) {
    value = checkPropAutofill(data, paramData)
  } else if (propType === 'input') {
    value = checkPropInput(data, paramData)
  } else if (propType === 'formData') {
    value = checkPropFormData(data, paramData)
  } else if (propType === 'search') {
    value = checkPropSearch(data, paramData)
  } else if (!propType) {
    value = checkPropSearchField(apiData, data, paramData)
  } else if (propType === 'system') {
    value = checkPropSystem(data, paramData)
  } else if (propType === 'urlParam') {
    value = checkPropUrlParam(data, paramData)
  } else if (propType === 'datasource') {
    value = checkPropDatasource(data, paramData)
  } else if (propType === 'tab') {
    value = checkPropTab(data, paramData)
  } else if (propType === 'list') {
    value = checkPropListID(data, idRef)
  } else if (propType === 'scoreChart') {
    value = checkPropScoreChart(data, paramData)
  } else if (propType === 'variables') {
    value = checkPropVariables(data, paramData)
  } else if (propType === 'postgrestql') {
    value = checkPropPostgrestql(data, paramData)
  } else if (propType === 'dashboard') {
    value = checkPropDashboard(data, paramData)
  } else if (propType === 'fileId') {
    value = checkPropFileId(data, paramData)
  } else if (propType === 'perPage') {
    value = checkPropPerPage(data, paramData)
  } else if (propType === 'page') {
    value = checkPropPage(data, paramData)
  } else if (propType === 'dateTime') {
    value = checkPropDateTime(data, paramData)
  } else if (propType === 'table') {
    value = checkPropTable(data, paramData)
  }
  return value
}

export const getQueryParamValue = (apiData, data, returnUrlParam = true) => {
  let { queryParams = [] } = apiData
  let query = returnUrlParam ? [] : {}
  let passApi = true
  queryParams.forEach((paramData) => {
    let { param, isRequireParam } = paramData
    if (!param) return
    let value = checkPropValue(apiData, data, paramData)
    if (returnUrlParam) {
      query = replaceQueryParamValue(query, paramData, value)
    } else {
      objectPath.set(query, param, value)
    }
    if (isRequireParam) {
      if (value === undefined || value === '' || value === null) {
        passApi = false
      }
    }
  })
  return { passApi, query }
}

const updateApiWithPath = (api, apiData, data) => {
  let { pathParams = [] } = apiData
  if (!pathParams) return api
  let passApi = true
  pathParams.forEach((paramData) => {
    let { param, propType, isRequireParam } = paramData
    if (!param || !propType) return
    let value = checkPropValue(apiData, data, paramData)
    api = replacePathParamValue(api, paramData, value)
    if (isRequireParam) {
      if (value === undefined || value === '' || value === null) {
        passApi = false
      }
    }
  })
  if (!passApi) return
  return api
}

export const updateApiWithQuery = (api, apiData, data) => {
  if (!api) return
  let { queryParams = [] } = apiData
  if (!queryParams) return api
  let { query, passApi } = getQueryParamValue(apiData, data)
  if (!passApi) return
  if (!query) return
  api = replaceQueryApi(api, apiData, query, data)
  return api
}

export const setButtonUrl = (url, action, data) => {
  url = updateApiWithPath(url, action, data)
  url = updateApiWithQuery(url, action, data)
  return url
}

export const setButtonApi = (apiData, data) => {
  let api = checkAPI(apiData)
  api = updateApiWithPath(api, apiData, data)
  api = updateApiWithQuery(api, apiData, data)
  return api
}

export const setListApi = (apiData, data) => {
  let api = checkAPI(apiData)
  api = updateApiWithPath(api, apiData, data)
  api = updateApiWithQuery(api, apiData, data)
  return api
}

export const getApi = (apiData, data, name) => {
  let api = checkAPI(apiData)
  api = updateApiWithPath(api, apiData, data)
  api = updateApiWithQuery(api, apiData, data)
  return api
}

export const setDashboardApi = (apiData, data) => {
  let api = checkAPI(apiData)
  api = updateApiWithPath(api, apiData, data)
  api = updateApiWithQuery(api, apiData, data)
  return api
}

export const setTableApi = (apiData, data) => {
  let api = checkAPI(apiData)
  api = updateApiWithPath(api, apiData, data)
  api = updateApiWithQuery(api, apiData, data)
  return api
}

const getAutoFillValue = ({
  keyword,
  key,
  param,
  jsonForm,
  formData,
  isDynamic,
  dynamicData,
}) => {
  let value
  if (objectPath.get(keyword, key)) {
    value = objectPath.get(keyword, `${key}`)
  } else if (objectPath.get(keyword, param)) {
    value = objectPath.get(keyword, `${param}`)
  } else if (isDynamic) {
    let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
    if (ui.key === key) {
      value = objectPath.get(dynamicData, ui.name)
    }
  } else if (objectPath.get(formData, param)) {
    value = objectPath.get(formData, `${param}`)
  } else {
    value = objectPath.get(formData, key)
  }
  return value
}

const getMapField = (param, searchData, formData) => {
  let { key, name } = param
  if (name) {
    return objectPath.get(searchData, name) || objectPath.get(formData, name)
  }
  return objectPath.get(searchData, key) || objectPath.get(formData, key)
}

export const setApi = (action, formData = {}, data = {}) => {
  let {
    apiType,
    api = '',
    service = '',
    endpoint = '',
    pathParams = [],
    queryParams = [],
    isTable = false,
    isOption = false,
    page = 1,
    perPage = 100,
    isDynamic,
    dynamicData,
    jsonForm,
  } = action
  let {
    allParams = {},
    urlParams = {},
    designerData = {},
    datasourceValue = {},
    tabParamActive = {},
    keyword = {},
    searchData = {},
  } = data

  if (apiType === 'core' || apiType === 'internal') {
    api = service + endpoint
  }
  let checkEmptyValue = false
  if (pathParams) {
    pathParams.forEach((pathParam) => {
      let {
        param,
        key,
        propType = '',
        value,
        prefixValue = '',
        isAutoFill = false,
        sourceKey,
        containerKey,
        mapParamType,
        customName,
        name,
      } = pathParam
      let valueKey = value
      if (!param) return
      if (mapParamType) {
        if (mapParamType === 'field') {
          value = getMapField(pathParam, searchData, formData)
        } else if (mapParamType === 'table') {
          if (objectPath.has(keyword, key)) {
            value = objectPath.get(keyword, key)
          } else if (keyword[key]) {
            value = keyword[key]
          } else {
            value = objectPath.get(formData, key)
          }
        }
      } else if (isAutoFill) {
        value = getAutoFillValue({
          keyword,
          key,
          param,
          jsonForm,
          formData,
          isDynamic,
          dynamicData,
        })
      } else {
        if (propType === 'tab') {
          if (containerKey) {
            value = checkPropTab(
              { designContext: { tabParamActive } },
              { containerKey, paramName: param },
            )
          } else {
            value = ''
          }
        } else if (propType === 'urlParam') {
          if (objectPath.has(allParams, key)) {
            value = objectPath.get(allParams, key)
          } else if (objectPath.has(urlParams, key)) {
            value = objectPath.get(urlParams, key)
          } else {
            value = ''
          }
        } else if (propType === 'scoreChart') {
          if (objectPath.has(allParams, key ?? param)) {
            value = objectPath.get(allParams, key ?? param)
          } else if (objectPath.has(urlParams, key ?? param)) {
            value = objectPath.get(urlParams, key ?? param)
          }
        } else if (propType === 'system') {
          value =
            objectPath.get(designerData, key) || objectPath.get(formData, key)
        } else if (propType === 'input' || propType === 'search') {
          value =
            (customName && objectPath.get(formData, customName)) ||
            (key && objectPath.get(formData, key)) ||
            (valueKey && objectPath.get(formData, valueKey)) ||
            (name && objectPath.get(formData, name)) ||
            (param && objectPath.get(formData, param)) ||
            (customName && objectPath.get(searchData, customName)) ||
            (key && objectPath.get(searchData, key)) ||
            (valueKey && objectPath.get(searchData, valueKey)) ||
            (name && objectPath.get(searchData, name)) ||
            (param && objectPath.get(searchData, param))
        } else if (propType === 'datasource') {
          value = objectPath.get(datasourceValue, `${sourceKey}.${key}`)
        } else if (propType === 'variables') {
          value = objectPath.get(formData, `${key}`)
        }
      }
      value = getQueryPrimitiveValue(value, pathParam)
      if (value && prefixValue && !String(value).startsWith(prefixValue)) {
        value = prefixValue + value
      }
      if (api.search(`:${param}`) > -1) {
        api = api.replace(`:${param}`, value)
      }
      if (api.search(`{${param}}`) > -1) {
        api = api.replace(`{${param}}`, value)
      }
      if (value === null || value === '' || value === undefined) {
        return (checkEmptyValue = true)
      }
    })
  }
  if (checkEmptyValue) {
    if (isTable) return api
    return null
  }
  let query = []
  if (queryParams) {
    queryParams.forEach((queryParam) => {
      let {
        param,
        name,
        key,
        propType = '',
        value,
        prefixValue = '',
        paramList = [],
        isAutoFill = false,
        sourceKey,
        containerKey,
        mapParamType,
        customName,
      } = queryParam
      let valueKey = value
      if (!param) return
      if (mapParamType) {
        if (mapParamType === 'field') {
          value = getMapField(queryParam, searchData, formData)
        } else if (mapParamType === 'table') {
          if (objectPath.has(keyword, key)) {
            value = objectPath.get(keyword, key)
          } else if (keyword[key]) {
            value = keyword[key]
          } else {
            value = objectPath.get(formData, key)
          }
        }
      } else if (isAutoFill) {
        value = getAutoFillValue({
          keyword,
          key,
          param,
          jsonForm,
          formData,
          isDynamic,
          dynamicData,
        })
      } else {
        if (propType === 'tab') {
          if (containerKey) {
            value = checkPropTab(
              { designContext: { tabParamActive } },
              { containerKey, paramName: param },
            )
          } else {
            value = ''
          }
        } else if (propType === 'urlParam') {
          if (objectPath.has(allParams, key)) {
            value = objectPath.get(allParams, key)
          } else if (objectPath.has(urlParams, key)) {
            value = objectPath.get(urlParams, key)
          } else {
            value = ''
          }
        } else if (propType === 'scoreChart') {
          value = objectPath.get(allParams, key ?? param)
        } else if (propType === 'input') {
          value =
            (customName && objectPath.get(formData, customName)) ||
            (key && objectPath.get(formData, key)) ||
            (valueKey && objectPath.get(formData, valueKey)) ||
            (name && objectPath.get(formData, name)) ||
            (param && objectPath.get(formData, param)) ||
            (customName && objectPath.get(searchData, customName)) ||
            (key && objectPath.get(searchData, key)) ||
            (valueKey && objectPath.get(searchData, valueKey)) ||
            (name && objectPath.get(searchData, name)) ||
            (param && objectPath.get(searchData, param))
        } else if (propType === 'search') {
          value =
            (customName && objectPath.get(searchData, customName)) ||
            (key && objectPath.get(searchData, key)) ||
            (valueKey && objectPath.get(searchData, valueKey)) ||
            (name && objectPath.get(searchData, name)) ||
            (param && objectPath.get(searchData, param)) ||
            (customName && objectPath.get(formData, customName)) ||
            (key && objectPath.get(formData, key)) ||
            (valueKey && objectPath.get(formData, valueKey)) ||
            (name && objectPath.get(formData, name)) ||
            (param && objectPath.get(formData, param))
        } else if (propType === 'datasource') {
          value = objectPath.get(datasourceValue, `${sourceKey}.${key}`)
        } else if (propType === 'system') {
          value =
            objectPath.get(designerData, key) || objectPath.get(formData, key)
        } else if (propType === 'postgrestql') {
          let paramValue = value
          let stringValue = ''
          paramList.forEach(({ param, key, propType = '', value }) => {
            if (propType === 'urlParam') {
              stringValue = objectPath.get(allParams, key)
            } else if (propType === 'input') {
              stringValue = objectPath.get(formData, key) || value
            } else if (propType === 'custom') {
              stringValue = value
            }
            if (stringValue === undefined) {
              stringValue = ''
            }
            paramValue = paramValue.replace(`$${param}`, stringValue)
          })
          value = paramValue
        } else if (propType === 'variables') {
          value = objectPath.get(formData, `${key}`)
        }
      }
      value = getQueryPrimitiveValue(value, queryParam)
      if (value === null || value === '' || value === undefined) return
      if (prefixValue && !String(value).startsWith(prefixValue)) {
        value = prefixValue + value
      }
      query.push(`${param}=${value}`)
    })
  }
  if (isOption) {
    let hasPage = queryParams.find(({ param }) => param === 'page')
    let hasPerPage = queryParams.find(({ param }) => param === 'perPage')
    if (!hasPage) {
      query.push(`page=${page}`)
    }
    if (!hasPerPage) {
      query.push(`perPage=${perPage}`)
    }
  }
  api = query.length > 0 ? api + '?' + query.join('&') : api
  return api
}

export const createAllGetApiGroup = (jsonForm) => {
  const apiGroup = jsonForm.apiGroup || []
  const apiCondition = jsonForm?.apiCondition || []
  const getConditionApi = apiCondition.filter((item) => item.method === 'get')
  return [...apiGroup, ...getConditionApi]
}

export const fetchAfterSuccess = async ({
  action,
  designContext,
  formData,
}) => {
  const requestVersion = getDesignerRequestVersion()
  const {
    jsonForm,
    searchData = {},
    designerData = {},
    urlParams = {},
    tabParamActive = {},
    user = {},
    setDatasourceValue,
  } = designContext
  const fetchSuccess = objectPath.get(action, 'fetchSuccess')
  const allApiGroup = createAllGetApiGroup(jsonForm)
  formData = formData ?? jsonForm.formData
  let datasourceValue = objectPath.get(designContext, '@datasource')

  if (!fetchSuccess) return
  let requestData = {
    searchData,
    formData,
    urlParams,
    designerData,
    datasourceValue,
    tabParamActive,
    user,
  }

  let promiseList = []
  let allData = { ...formData }
  Object.keys(fetchSuccess).forEach(async (key) => {
    const item = allApiGroup.find((api) => api.key === key)
    if (!item) return
    let promise = new Promise(async (resolve, reject) => {
      let response = {}
      let data = null
      let status = null
      let message = ''
      let awaitPromised = await Promise.all(promiseList)
      awaitPromised.forEach((data) => {
        if (data?.formData) {
          allData = data?.formData
        } else {
          allData = formData
        }
      })
      let api = setApi({ ...item, jsonForm }, formData, requestData)
      if (!api) return resolve(formData)
      let { headers = [] } = item
      let config = {}
      if (headers.length > 0) {
        headers.forEach(({ key, value }) => {
          if (key) {
            objectPath.set(config, `headers.${key}`, value)
          }
        })
      }
      config = applyRequestTimeout(config, item)
      response = await withRequestTimeout(httpClient.get(api, config), item)
        .then((data) => data)
        .catch(function (error) {
          let errorResponse = {
            status: 500,
            message: 'There was an error!',
            data: null,
          }
          if (error.response) {
            errorResponse = {
              status: error.response.status,
              message: error.response.data,
              data: null,
            }
          }
          return errorResponse
        })
      if (!response || !isDesignerRequestVersionActive(requestVersion)) {
        return reject({ data, item, response, formData, isCancel: true })
      }

      data = response.data
      message = response.message
      status = response.status
      if (status >= 400) return reject({ data, item, response, formData })
      formData = mapResponseData(
        { ...item, api },
        data,
        formData,
        {},
        jsonForm,
        designContext,
      )
      resolve({ data, item, response, formData })
    })
      .then((values) => values)
      .catch((error) => {
        if (error?.isCancel) return null
        console.error('There was an error!', error)
      })
    promiseList.push(promise)
  })
  await Promise.all(promiseList).then((resolveData) => {
    if (!isDesignerRequestVersionActive(requestVersion)) return
    resolveData.forEach((res) => {
      if (res) {
        let { data = null, item, response } = res
        if (item) {
          if (item.dataKey) {
            data = objectPath.get(data, item.dataKey)
          }
          objectPath.set(datasourceValue, item.key, data)
          objectPath.set(formData, `@response.${item.key}`, response.data)
          if (setDatasourceValue) {
            setDatasourceValue(datasourceValue)
          }
        }
      }
    })
  })
}
